import { memo, useMemo, useState } from "react";
import { Dropdown, Flex, Select } from "@repo/ping-design";
import {
  MIN_ZOOM_REQUIRED_FOR_VEXCEL,
  isInUS,
  SpecialComponentProps,
} from "@repo/ping-react-js";
import { DropdownSelectionType } from "@repo/ping-design";
import "./VexcelImagerySelector.scss";

interface VexcelImagerySelectorProps extends SpecialComponentProps {
  zoom?: number;
  center?: { lat: number; lng: number };
  onDateSelect?: (date: string) => void;
  availableLayers?: Record<string, any>;
  enabledLayer?: { name: string } | null;
  vexcelDates?: Array<{ date: string; id: string; [key: string]: any }>;
  isLoadingVexcel?: boolean;
}

export const VexcelImagerySelector = memo(
  ({
    onLayerSelect,
    zoom,
    center,
    onDateSelect,
    availableLayers,
    enabledLayer,
    vexcelDates = [],
    isLoadingVexcel = false,
  }: VexcelImagerySelectorProps) => {
    const usBasedLocation = center && isInUS(center);
    const canViewVexcel = !!availableLayers?.VEXCEL;

    const dropdownOptions = useMemo(
      () =>
        vexcelDates.map((dateObj) => ({
          label: dateObj.date,
          value: dateObj.id,
        })),
      [vexcelDates],
    );

    const [selectedDateId, setSelectedDateId] = useState<string | null>(
      vexcelDates[0]?.id || null,
    );

    const handleDateChange = (selection: any) => {
      if (selection.type === DropdownSelectionType.SimpleValue) {
        const dateId = selection.value;
        setSelectedDateId(dateId);
        const dateObj = vexcelDates.find((d) => d.id === dateId);
        if (dateObj) {
          onDateSelect?.(dateObj.date);
        }
      }
    };

    const showDateDropdown =
      vexcelDates.length > 0 &&
      zoom !== undefined &&
      zoom >= MIN_ZOOM_REQUIRED_FOR_VEXCEL;

    const viewOptions = [
      { label: "Vertical", value: "vertical-vexcel" },
      { label: "North", value: "north-vexcel" },
      { label: "South", value: "south-vexcel" },
      { label: "East", value: "east-vexcel" },
      { label: "West", value: "west-vexcel" },
    ];

    const handleViewChange = (_: any, value: string) => {
      onLayerSelect(
        {
          label: value,
          name: value,
          url: [],
          layerType: "google",
          id: value,
          zoomIntoBuilding: true,
        },
        { zoomIntoBuilding: true },
      );
    };

    const renderViewAndDateSelectors = () => (
      <Flex
        direction="column"
        gap="3"
        className="VexcelImagerySelector__Dropdowns"
      >
        <Select
          options={viewOptions}
          value={enabledLayer?.name || ""}
          onChange={handleViewChange}
          label="View"
          field="view"
          disabled={isLoadingVexcel || dropdownOptions.length === 0}
        />
        <Dropdown
          options={dropdownOptions}
          selected={selectedDateId}
          onChange={handleDateChange}
          placeholder="mm dd yyyy"
          label="Select Date"
          iconName="Calendar"
          disabled={isLoadingVexcel || dropdownOptions.length === 0}
        />
      </Flex>
    );

    return (
      <div className="VexcelImagerySelector">
        <h3 className="VexcelImagerySelector__Title">Vexcel Imagery</h3>

        {!canViewVexcel && (
          <p className="VexcelImagerySelector__Error">
            Vexcel imagery is not available for your account.
          </p>
        )}

        {canViewVexcel && !usBasedLocation && (
          <p className="VexcelImagerySelector__Error">
            Vexcel imagery is only available for US locations.
          </p>
        )}

        {canViewVexcel &&
          usBasedLocation &&
          zoom !== undefined &&
          zoom < MIN_ZOOM_REQUIRED_FOR_VEXCEL && (
            <>
              {renderViewAndDateSelectors()}
              <p className="VexcelImagerySelector__Error">
                Zoom in to enable Vexcel imagery.
              </p>
            </>
          )}

        {showDateDropdown && renderViewAndDateSelectors()}

        {!showDateDropdown &&
          canViewVexcel &&
          usBasedLocation &&
          zoom !== undefined &&
          zoom >= MIN_ZOOM_REQUIRED_FOR_VEXCEL && (
            <>
              {renderViewAndDateSelectors()}
              <p className="VexcelImagerySelector__Error">
                No Vexcel dates available for this location.
              </p>
            </>
          )}
      </div>
    );
  },
);

VexcelImagerySelector.displayName = "VexcelImagerySelector";
